import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import useSearchFilters from '../../../hooks/useSearchFilters';

// Components
import QuoteCta from '../content/QuoteCta';
import FeaturedTrucks from '../FeaturedTrucks';
import CuisineLink from '../content/CuisineLink';
import SimpleCta from '../content/SimpleCta';
import FeaturedPods from '../FeaturedPods';
import CateringFaq from '../faqs/CateringFaq';
import CateringCta from '../content/CateringCta';
import Meta from '../../shared/Meta';
import SearchFilters from './SearchFilters';

import cuisineLinkStyles from '../content/CuisineLink.module.postcss';
import styles from './Explore.module.postcss';

const sliderSettings = {
  slidesToShow: 3,
  slidesToScroll: 1,
};

const featuredOptions = { page_size: 3, featured: true };
const sharedVendorOptions = { page_size: 3, has_upcoming_events: true };

const allOptions = { ...sharedVendorOptions };
const sandwichOptions = { food_categories: 'sandwiches', ...sharedVendorOptions };
const tacoOptions = { food_categories: 'tacos', ...sharedVendorOptions };
const bbqOptions = { food_categories: 'bbq', ...sharedVendorOptions };
const dessertOptions = { food_categories: 'dessert', ...sharedVendorOptions };
const burgerOptions = { food_categories: 'hamburgers', ...sharedVendorOptions };

function ExploreFoodCategory({ title, text, options, link, linkText, h1 }) {
  return (
    <section className='Section'>
      { h1 ? <h1>{title}</h1> : <h2>{title}</h2> }
      <p>{text}</p>
      <FeaturedTrucks
        noTitle
        noWrapper
        showMenuLinks
        sliderSettings={sliderSettings}
        options={options}
      />
      <div className='u-textCenter u-mt2'>
        <Link to={link} className='Button'>{linkText}</Link>
      </div>
    </section>
  );
}

function Explore() {
  const { searchFilters, setFilters } = useSearchFilters({ sendToSearchPage: true });
  const { city_name, vehicle_type, site_title } = useContext(ConfigContext);
  const vehicleLowerCase = vehicle_type.toLowerCase();

  return (
    <div className={styles.Explore}>
      <Meta
        title={`🚚 ${city_name}'s Best Food ${vehicle_type}s - Find & Book Now!`}
        breadcrumbName='Explore'
        type='website'
        description={`Looking for ${city_name} food ${vehicleLowerCase} catering for your next event? Whether it's a wedding, corporate lunch, private party, or community festival, we make booking fast, easy, and stress-free. ${site_title} connects you with top-rated ${city_name} food ${vehicleLowerCase}s, offering authentic, diverse, and delicious options tailored to your event.`}
      />

      <SearchFilters
        filters={searchFilters}
        setFilters={setFilters}
        noMapToggle
      />

      <section className='Section u-pt05 u-pb0'>
        <QuoteCta />
      </section>

      <FeaturedTrucks
        title='Sponsored Results'
        className='Section'
        showMenuLinks
        options={featuredOptions}
        sliderSettings={sliderSettings}
        noLazy
      />

      <ExploreFoodCategory
        title={`🚚 ${city_name}'s Best Food ${vehicle_type}s - Find & Book Now!`}
        text={`Looking for ${city_name} food ${vehicleLowerCase} catering for your next event? Whether it's a wedding, corporate lunch, private party, or community festival, we make booking fast, easy, and stress-free. ${site_title} connects you with top-rated ${city_name} food ${vehicleLowerCase}s, offering authentic, diverse, and delicious options tailored to your event.`}
        options={allOptions}
        link='/category/all'
        linkText={`Explore All Food ${vehicle_type}s`}
        h1
      />

      <section className='Section'>
        <h3 className='u-textCenter u-phosphate u-mb2'>{city_name}&#39;s Best Food {vehicle_type}s By Cuisine</h3>
        <div className={cuisineLinkStyles.CuisineLinkContainer}>
          <CuisineLink title='Tacos' emoji='&#127790;' link='/taco-trucks' />
          <CuisineLink title='Sandwiches' emoji='&#129386;' link='/sandwich-food-trucks' />
          <CuisineLink title='Desserts' emoji='&#127846;' link='/dessert-food-trucks' />
          <CuisineLink title='Burgers' emoji='&#127828;' link='/burger-food-trucks' />
          <CuisineLink title='BBQ' emoji='&#127830;' link='/bbq-food-trucks' />
        </div>
      </section>

      <ExploreFoodCategory
        title={`🥪 Sandwich Food ${vehicle_type}s`}
        text='Indulge in hearty sandwiches filled with bold flavors, from classic deli-style to creative twists.'
        options={sandwichOptions}
        link='/sandwich-food-trucks'
        linkText={`Explore Sandwich Food ${vehicle_type}s`}
      />

      <ExploreFoodCategory
        title={`🌮 Taco Food ${vehicle_type}s`}
        text='Savor authentic, street-style tacos packed with fresh ingredients and vibrant flavors.'
        options={tacoOptions}
        link='/taco-trucks'
        linkText={`Explore Tacos Food ${vehicle_type}s`}
      />

      <ExploreFoodCategory
        title={`🍖 BBQ Food ${vehicle_type}s`}
        text='Experience smoky, tender BBQ with delicious meats slow-cooked to perfection.'
        options={bbqOptions}
        link='/bbq-food-trucks'
        linkText={`Explore BBQ Food ${vehicle_type}s`}
      />

      <ExploreFoodCategory
        title={`🍰 Dessert Food ${vehicle_type}s`}
        text={`Treat yourself to decadent desserts from food ${vehicleLowerCase}s offering indulgent sweets and baked goods.`}
        options={dessertOptions}
        link='/dessert-food-trucks'
        linkText={`Explore Dessert Food ${vehicle_type}s`}
      />

      <ExploreFoodCategory
        title={`🍔 Burger Food ${vehicle_type}s`}
        text='Enjoy juicy, gourmet burgers made with the freshest ingredients and served hot off the grill.'
        options={burgerOptions}
        link='/burger-food-trucks'
        linkText={`Explore Burger Food ${vehicle_type}s`}
      />

      <section className='Section'>
        <SimpleCta
          text={`Can't find your food ${vehicleLowerCase}? Add your business for free!`}
          link='/vendor/register'
          linkTitle='Join Now'
        />
      </section>

      { city_name === 'Seattle' &&
        <section className='Section'>
          <h2>🚚 Seattle&#39;s Food Truck Scene: Where Flavor Meets Innovation</h2>
          <p>Seattle isn&#39;t just about coffee and rain – it&#39;s a city that thrives on bold flavors, local ingredients, and creative street food. From the tech hubs of South Lake Union to the laid-back breweries in Ballard, food trucks have become a defining part of the city&#39;s culinary scene.</p>
          <p>With a culture that values fresh, locally sourced food, Seattleites don&#39;t settle for just any meal. They want handcrafted burgers made from grass-fed beef, Thai curries bursting with spice, tacos that rival Mexico City, and wood-fired pizzas that transport you straight to Naples. Thankfully, Seattle&#39;s food trucks deliver exactly that.</p>
          <p>Now that the food truck movement has taken over the city, there are almost too many options to choose from. From global fusion to classic American comfort food, each truck brings something unique to the table. But what unites them all is a commitment to artisanal flavors, high-quality ingredients, and forward-thinking culinary techniques.</p>
        </section>
      }

      { city_name === 'Seattle' &&
        <section className='Section'>
          <h2>🍽️ Seattle&#39;s Must-Try Food Trucks</h2>
          <p>Seattle&#39;s food truck scene is packed with culinary innovation, offering everything from classic comfort food to bold global flavors. If you&#39;re in the mood for crispy, golden British-style fish & chips, Nosh is a local favorite, even earning Seattle Times&#39; Reader&#39;s Choice for Best Food Truck. Craving Southern comfort? Seattle Biscuit Company serves up flaky, buttery biscuits topped with everything from sausage gravy to eggs, ham, and cheese grits. For a taste of New Orleans, Where Ya At Matt delivers authentic po&#39;boys, gumbo, jambalaya, and sugar-dusted beignets that bring a taste of the South to the Pacific Northwest.</p>
          <p>If spicy, fragrant Thai food is more your style, Thai-U-Up is a must-visit, known for its volcano chicken, rich yellow curry, and cult-favorite Pad Thai. Those looking for something unique will love Papa Bois, where South Asian, Cuban, and Caribbean flavors collide in dishes like the Orient Boat—slow-cooked pork in a lemongrass-coconut marinade over pistachio rice. For Indian street food lovers, NaanSense offers lamb meatball curry, butter chicken, and seasonal rotating specialties, all crafted for easy, on-the-go eating.</p>
          <p>For those who believe everything is better with cheese, Cheese Wizards is Seattle&#39;s top spot for grilled cheese sandwiches with over-the-top flavors, featuring creations like the Goblin King—a melty mix of Swiss, Jack, cream cheese, grilled chicken, and red peppers. Meanwhile, Napkin Friends takes things up a notch with pressed-latke sandwiches, replacing traditional bread with crispy, golden potato pancakes. For a healthier take on street food, I Love My GFF offers gluten-free, paleo, and vegan-friendly quinoa bowls, packed with locally sourced, nutrient-rich ingredients.</p>
          <p>Seattle&#39;s food trucks are more than just a meal — they&#39;re an experience. Whether you&#39;re looking for classic street food, bold international flavors, or innovative fusion dishes, there&#39;s no shortage of must-try trucks ready to serve up something unforgettable.</p>
        </section>
      }

      { city_name === 'Seattle' &&
        <section className='Section--extraPadding'>
          <FeaturedPods />
        </section>
      }

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta />
      </section>

    </div>
  );
}

Explore.propTypes = {

};

export default Explore;
